import { Box } from '@mui/material';
import { addListener } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Checkout from 'components/Checkout';
import EmailConfirmationDialog from 'components/EmailConfirmationDialog';
import aiMusicGeneratorApi from 'services/api/aiMusicGenerator/aiMusicGeneratorApi';
import useFeature from 'services/hooks/feature';
import { type AppDispatch } from 'services/store';
import { actions } from 'services/store/ai';
import { PromptType } from 'services/store/ai/types';
import MobileFormulaDrawer from './components/MobileFormulaDrawer';
import Bundles from './components/Bundles';
import Sidebar from './components/Sidebar';
import { AIGenerationLimitDialog } from './components/Sidebar/components/AIGenerationLimitDialog';
import useFormulaSubmit, {
  triggerAIGenerationLimitReached,
} from './components/Sidebar/useFormulaSubmit';
import { parseFormula } from './helpers/formulaUrl';
import { Container, InnerContainer } from './styled';

const CANONICAL_PATHNAME = '/ai-music-generator';

const CanonicalUrl = () => {
  const { origin } = window.location;
  const canonicalUrl = `${origin}${CANONICAL_PATHNAME}`;

  return (
    <>
      {/* @ts-ignore */}
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
    </>
  );
};

const AIMusicGenerator = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { state, search, hash } = useLocation();

  const { data: genres = [] } =
    aiMusicGeneratorApi.useGetAiMusicGeneratorGenresQuery();

  aiMusicGeneratorApi.useGetAiMusicGeneratorSongStructuresQuery();

  const submitHandler = useFormulaSubmit();

  const { isMicroGenrePaywallEnabled } = useFeature();

  const [isAIGenerationLimitDialogOpen, setIsAIGenerationLimitDialogOpen] =
    useState(false);

  const [isEmailConfirmedDialogOpen, setIsEmailConfirmedDialogOpen] =
    useState(false);

  useEffect(() => {
    if (state?.showEmailConfirmationDialog) {
      setIsEmailConfirmedDialogOpen(true);
    }
  }, [state]);

  useEffect(() => {
    if (hash === `#${PromptType.TextToMusic}`) {
      dispatch(actions.setPromptType(PromptType.TextToMusic));
    } else {
      dispatch(actions.setPromptType(PromptType.AdvancedFormula));
    }
  }, [hash, dispatch]);

  useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        actionCreator: triggerAIGenerationLimitReached,
        effect: () => setIsAIGenerationLimitDialogOpen(true),
      })
    );

    return () => {
      unsubscribe();
      dispatch(actions.resetCurrentTrack());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!search) {
      return;
    }

    if (!genres.length) {
      return;
    }

    const { primaryGenre, microGenre, formula } = parseFormula(search);

    const genre = genres.find(({ name }) => name === primaryGenre);
    const primaryGenreIsValid = primaryGenre && !!genre;

    if (primaryGenreIsValid) {
      dispatch(actions.setPrimaryGenre({ primaryGenre }));
    }

    const microGenreIsValid =
      microGenre && genre?.microGenres.find(({ name }) => name === microGenre);

    if (microGenreIsValid && !isMicroGenrePaywallEnabled) {
      dispatch(actions.setMicroGenre({ microGenre }));
    }

    dispatch(actions.setFormula({ formula }));

    if (/[?|&]autostart/.test(window.location.search)) {
      submitHandler();
    }

    window.history.replaceState(null, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, genres, isMicroGenrePaywallEnabled]);

  return (
    <>
      <CanonicalUrl />
      <Container>
        <InnerContainer>
          {isMobile ? <MobileFormulaDrawer /> : <Sidebar />}
          <Box flex={1}>
            <Bundles />
          </Box>
        </InnerContainer>
      </Container>
      <EmailConfirmationDialog
        isOpen={isEmailConfirmedDialogOpen}
        onClose={() => setIsEmailConfirmedDialogOpen(false)}
      />
      <AIGenerationLimitDialog
        isOpen={isAIGenerationLimitDialogOpen}
        onClose={() => setIsAIGenerationLimitDialogOpen(false)}
      />
      <Checkout shouldBlockRedirect />
    </>
  );
};

export default AIMusicGenerator;
